/**
 * Defines a custom HTML element 'sorting-dropdown' for creating a sorting dropdown menu.
 * This component is used for sorting items on a webpage, typically in a collection or list.
 * It allows users to select a sorting option from a dropdown menu, updating the page content accordingly.
 *
 * Properties:
 * - selectedOptionClass: String - CSS class to apply to the selected sorting option.
 * - defaultLabel: String - The default label for the dropdown button, typically used to reset the selection.
 * - button: HTMLElement - The button element that triggers the dropdown.
 * - dropdown: HTMLElement - The container element for the dropdown menu options.
 * - backdrop: HTMLElement - The backdrop element used to close the dropdown when clicked outside.
 * - options: Array<HTMLElement> - An array of list item elements representing the sorting options.
 * - form: HTMLElement - The form element that is updated when a sorting option is selected.
 *
 *
 * Example:
 * ```liquid
 * <!-- In a Liquid template file -->
 *
 * <sorting-dropdown
 *   class="group relative inline-block"
 *   data-selected-option-class="font-semibold"
 *   data-default-label="Sort"
 * >
 *   <button class="dropdown-button" aria-haspopup="true" aria-expanded="false">...</button>
 *   <ul class="dropdown-menu" aria-labelledby="dropdown-button" role="menu">
 *       <li
 *        role="menuItem"
 *        class="cursor-pointer whitespace-nowrap states:opacity-70"
 *        data-value="price-asc"
 *      >
 *        Price Ascending
 *      </li>
 *   </ul>
 * </sorting-dropdown>
 * ```
 **/
if (!customElements.get('sorting-dropdown')) {
  customElements.define(
    'sorting-dropdown',
    class SortingDropdown extends HTMLElement {
      constructor() {
        super();

        this.selectedOptionClass = this.dataset.selectedOptionClass;
        this.defaultLabel = this.dataset.defaultLabel;
        this.button = this.querySelector('.dropdown-button');
        this.dropdown = this.querySelector('.dropdown-menu');
        this.backdrop = this.querySelector('.dropdown-backdrop');
        this.options = Array.from(this.dropdown.querySelectorAll('li'));

        this.button.addEventListener('click', this.onClick.bind(this));
        this.backdrop.addEventListener('click', this.onBackdropClick.bind(this));

        this.options.forEach((option) => {
          option.addEventListener('click', this.onOptionSelected.bind(this));
        });

        this.init();
      }

      onOptionSelected(event) {
        const value = event.target.dataset.value;
        const name = event.target.textContent;
        this.button.textContent = name;

        this.options.forEach((option) => {
          if (option !== event.target) option.classList.remove(this.selectedOptionClass);
          else option.classList.add(this.selectedOptionClass);
        });

        this.close();
        this.setFormValue(value);
      }

      onClick() {
        this.classList.add('is-open');
        this.dropdown.ariaHidden = false;
        this.button.ariaExpanded = true;
      }

      onBackdropClick() {
        this.close();
      }

      close() {
        this.classList.remove('is-open');
        this.button.ariaExpanded = false;
        this.dropdown.ariaHidden = true;
      }

      init() {
        const params = this.getParams();
        const value = params.get('sort_by');
        const item = this.options.find((e) => e.dataset.value === value);

        if (!item) return;

        this.button.textContent = item.textContent;
        item.classList.add(this.selectedOptionClass);
      }

      getParams() {
        const url = new URL(window.location.href);
        return new URLSearchParams(url.search);
      }

      setFormValue(value) {
        // update the url
        const params = this.getParams();
        params.set('sort_by', value);

        // Render the page again with method from search filters form component
        this.form = document.querySelector('filters-form');
        this.form.renderPage(params.toString());
      }

      reset() {
        this.options.forEach((e) => e.classList.remove(this.selectedOptionClass));
        this.button.textContent = this.defaultLabel;
      }
    }
  );
}
